/**
 * Unauthenticated account endpoints (password recovery, email confirmation).
 * Activation lives on `AuthService.activate`, because it starts a session. These use plain `fetch`: `fetchApi` waits for the auth
 * session to be ready, which never happens on the public pages.
 */

/** A 4xx/5xx answer from the auth service, with its `{ error }` message. */
export class AccountAccessError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'AccountAccessError';
  }
}

async function publicPost<T = void>(path: string, body: object): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    try {
      const data = await response.json();
      if (typeof data?.error === 'string') message = data.error;
    } catch {
      // not JSON, keep the generic message
    }
    throw new AccountAccessError(response.status, message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Mails a password-recovery link to the account matching `identifier`
 * (loginname or email). Always succeeds, so account existence is not leaked.
 */
export function requestPasswordRecovery(identifier: string) {
  return publicPost('/request-recover-password', { identifier });
}

/** Sets a new password using the token from the recovery (or reset) mail. */
export function recoverPassword(token: string, password: string) {
  return publicPost('/recover-password', { token, password });
}

/** Confirms a pending email change using the token from the verification mail. */
export function validateEmail(token: string) {
  return publicPost('/validate-email', { token });
}
